import {
  type RefObject,
  useCallback,
  useRef,
  useSyncExternalStore,
} from 'react';

interface Dimensions {
  width: number;
  height: number;
}

export const useDimensions = <ElementType extends HTMLElement>(): [
  ref: RefObject<ElementType | null>,
  dimensions: Dimensions,
] => {
  const ref = useRef<ElementType | null>(null);
  const dimensionsRef = useRef<Dimensions>({ width: 0, height: 0 });

  const subscribe = useCallback((callback: () => void): (() => void) => {
    if (ref.current === null) {
      return () => {};
    }

    const resizeObserver = new ResizeObserver(callback);
    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getSnapshot = useCallback((): Dimensions => {
    if (ref.current === null) {
      dimensionsRef.current = { width: 0, height: 0 };
      return dimensionsRef.current;
    }

    const width = ref.current.offsetWidth;
    const height = ref.current.offsetHeight;

    if (
      dimensionsRef.current.width !== width ||
      dimensionsRef.current.height !== height
    ) {
      dimensionsRef.current = { width, height };
    }

    return dimensionsRef.current;
  }, []);

  const dimensions: Dimensions = useSyncExternalStore(subscribe, getSnapshot);

  return [ref, dimensions];
};
